import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import type { WidgetSettings } from '@teo/widget-shared';
import {
  getLocalizedText,
  parseWidgetSettings,
  resolveSiteLocale,
  settingsToCssVars,
  type WidgetSettingsResponse,
} from '@teo/widget-shared';
import { getUiStrings } from '../i18n/ui-strings.js';
import { prefetchAvatar } from '../utils/avatar-image.js';

type WidgetContextValue = {
  settings: WidgetSettings;
  cssVars: Record<string, string>;
  locale: string;
  t: ReturnType<typeof getUiStrings>;
  welcomeText: string;
  exitIntentText: string;
};

const WidgetContext = createContext<WidgetContextValue | null>(null);

export function WidgetProvider({
  rawSettings,
  initialLocale,
  children,
}: {
  rawSettings: WidgetSettingsResponse;
  /** Override auto-detection (Storybook / tests only) */
  initialLocale?: string;
  children: ReactNode;
}) {
  const settings = useMemo(
    () => parseWidgetSettings(rawSettings),
    [rawSettings],
  );
  const cssVars = useMemo(() => settingsToCssVars(settings), [settings]);

  useEffect(() => {
    prefetchAvatar(settings.content.aiAvatarUrl);
  }, [settings.content.aiAvatarUrl]);

  const locale = useMemo(
    () =>
      resolveSiteLocale(settings.content.languages, { initialLocale }),
    [settings.content.languages, initialLocale],
  );

  const value = useMemo<WidgetContextValue>(
    () => ({
      settings,
      cssVars,
      locale,
      t: getUiStrings(locale),
      welcomeText: getLocalizedText(
        settings.content.welcomeMessage,
        locale,
      ),
      exitIntentText: getLocalizedText(
        settings.content.exitIntentMessage,
        locale,
      ),
    }),
    [settings, cssVars, locale],
  );

  return (
    <WidgetContext.Provider value={value}>{children}</WidgetContext.Provider>
  );
}

export function useWidget(): WidgetContextValue {
  const ctx = useContext(WidgetContext);
  if (!ctx) {
    throw new Error('useWidget must be used within WidgetProvider');
  }
  return ctx;
}
